import { useCallback, useEffect, useState } from 'react';
import { CloudUpload } from '../lib/icons';
import { fmt, PageTitle } from '../components/shared/badges';
import { DEFAVORABLE, NEUTRE, NEUTRE_DOUX } from '../lib/couleurs';
import { chargerRessource } from '../lib/passerelle';
import { useSondage } from '../lib/sondage';
import { DocumentsPasserelle } from '../components/passerelle/DocumentsPasserelle';
import { ExceptionsPasserelle } from '../components/passerelle/ExceptionsPasserelle';
import { BanquePasserelle } from '../components/passerelle/BanquePasserelle';
import { ConnexionDrive, DepotPasserelle, JournalPasserelle, PiecesPasserelle } from '../components/passerelle/BlocsPasserelle';
import { SupervisionPasserelle } from '../components/passerelle/SupervisionPasserelle';

import { PropositionsReglement } from '../components/passerelle/PropositionsReglement';

const ONGLETS = [
  ['propositions', 'Propositions de règlement'],
  ['documents', 'Documents'],
  ['exceptions', 'Exceptions'],
  ['pieces', 'Pièces & obligations'],
  ['banque', 'Rapprochement bancaire'],
  ['depot', 'Dépôt web / mobile'],
  ['supervision', 'Supervision & sauvegardes'],
  ['journal', 'Historique des décisions'],
];

export default function Passerelle() {
  const [onglet, setOnglet] = useState('documents');
  const [etat, setEtat] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [pieces, setPieces] = useState([]);
  const [obligations, setObligations] = useState([]);
  const [journal, setJournal] = useState([]);
  const [erreurs, setErreurs] = useState([]);
  const [chargement, setChargement] = useState(true);

  const load = useCallback(async () => {
    const sections = [
      ['Indicateurs', 'etat', setEtat],
      ['Documents', 'documents', setDocuments],
      ['Exceptions', 'exceptions?etat=ouverte', setExceptions],
      ['Pièces métier', 'pieces', setPieces],
      ['Obligations', 'obligations', setObligations],
      ['Historique des décisions', 'journal', setJournal],
    ];
    const echecs = [];
    const releve = await Promise.all(
      sections.map(async ([libelle, chemin, appliquer]) => {
        const { error, data } = await chargerRessource(chemin);
        if (error) {
          echecs.push(`${libelle} (${error})`);
          return `${chemin}:erreur`;
        }
        appliquer(data);
        return `${chemin}:${Array.isArray(data) ? data.length : JSON.stringify(data ?? '')}`;
      }),
    );
    setErreurs(echecs);
    setChargement(false);
    return releve;
  }, []);

  useEffect(() => {
    load();
  }, [load]);
  useSondage(load, 15000);

  return (
    <div data-testid="page-passerelle" className="space-y-5">
      <PageTitle
        emoji="📥"
        titre="Passerelle Drive"
        sous="La comptable dépose ses documents ; le serveur archive l’original, extrait les informations, crée ou rapproche les opérations certaines et présente les cas ambigus ici."
      />

      {erreurs.length > 0 && (
        <p data-testid="passerelle-erreurs" className="border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          <strong>Lecture serveur en échec :</strong> {erreurs.join(' · ')}. Les sections concernées restent vides — elles ne
          sont donc pas « sans donnée » : la page réessaie toutes les 15 secondes.
        </p>
      )}

      {etat && (
        <div className="grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-6">
          {[
            ['Documents traités automatiquement', String(etat.documents.traites), NEUTRE],
            ['En file de traitement', String(etat.documents.en_attente + etat.jobs.en_attente), NEUTRE_DOUX],
            ['Justificatifs rattachés', String(etat.pieces.justificatifs_rattaches), NEUTRE],
            ['Montants non affectés', `${fmt(etat.montants_non_affectes.total_tnd ?? 0, 2)} TND`, DEFAVORABLE],
            ['Exceptions à résoudre', String(etat.exceptions_ouvertes), etat.exceptions_ouvertes ? 'text-amber-800' : NEUTRE],
            ['Dernière sync Drive réussie', etat.drive.derniere_sync_reussie ? String(etat.drive.derniere_sync_reussie).slice(0, 16).replace('T', ' ') : 'Jamais', NEUTRE_DOUX],
          ].map(([k, v, cls]) => (
            <div key={k} className="bg-white px-3 py-2.5">
              <p className="text-[11px] text-slate-500">{k}</p>
              <p className={`tabular font-data text-base font-semibold ${cls}`}>{v}</p>
            </div>
          ))}
        </div>
      )}

      <ConnexionDrive onMaj={load} />

      <nav data-testid="passerelle-onglets" className="flex flex-wrap gap-1 border-b border-slate-200">
        {ONGLETS.map(([k, label]) => (
          <button
            key={k}
            type="button"
            data-testid={`passerelle-onglet-${k}`}
            onClick={() => setOnglet(k)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm transition-colors duration-150 ${
              onglet === k ? 'border-slate-900 font-semibold text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
          >
            {label}
            {k === 'exceptions' && exceptions.length > 0 && (
              <span className="ml-1.5 border border-amber-300 bg-amber-50 px-1 font-data text-[10px] text-amber-800">{exceptions.length}</span>
            )}
          </button>
        ))}
      </nav>

      {onglet === 'propositions' && <PropositionsReglement />}
      {onglet === 'documents' && <DocumentsPasserelle documents={documents} onMaj={load} />}
      {onglet === 'exceptions' && <ExceptionsPasserelle exceptions={exceptions} onMaj={load} />}
      {onglet === 'pieces' && <PiecesPasserelle pieces={pieces} obligations={obligations} chargement={chargement} />}
      {onglet === 'banque' && <BanquePasserelle onMaj={load} />}
      {onglet === 'depot' && <DepotPasserelle onMaj={load} />}
      {onglet === 'supervision' && <SupervisionPasserelle />}
      {onglet === 'journal' && <JournalPasserelle journal={journal} />}

      <p className="flex items-start gap-2 border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
        <CloudUpload size={14} className="mt-0.5 flex-shrink-0" />
        Les trois canaux (Drive, photo mobile, saisie web) utilisent les mêmes services serveur de création et de rapprochement.
        La passerelle enregistre et rapproche : elle n’initie aucun transfert d’argent.
      </p>
    </div>
  );
}
